import React, { useState, useRef, useCallback, useMemo, forwardRef, useImperativeHandle, useEffect } from 'react';
import { IoClose, IoChevronBack } from 'react-icons/io5';
import { FiEdit2 } from 'react-icons/fi';
import { FcGoogle } from 'react-icons/fc';
import KitButton from '../kit/Button';
import IconButton from '../kit/IconButton';
import { Card } from '../kit/Card';
import axiosInstance from '../../api/axiosInstance';
import { backendUrl } from '../../api/constants';
import Input from '../Input/Input';
import DashboardMap from '../DashboardMap/DashboardMap';
import Loader from '../icons/Loader';
import Logo from '../../assets/imgs/inyc.svg';
import eventMarker from '../../assets/map/event-marker.png';
import { formatAddressComponents, validateAddressComponents, parseGoogleAddress, parseAddressString } from 'lib';
import { Popup } from 'react-leaflet';
import { MdClear } from 'react-icons/md';
import { MarkerPopupButton } from '../Map/MapMarker';
import './LocationSelector.css';

const normalizeGoogleName = (value = '') => value.trim().toLowerCase().replace(/\s+/g, ' ');

const isAddressLikeGoogleName = (name = '', formattedAddress = '') => {
  const normalizedName = normalizeGoogleName(name);
  if (!normalizedName) return true;

  const normalizedAddress = normalizeGoogleName(formattedAddress);
  if (normalizedAddress && normalizedAddress.startsWith(normalizedName)) return true;

  return /^\d+\s/.test(normalizedName);
};

const EMPTY_ADDRESS_FIELDS = {
  venueName: '',
  streetAddress: '',
  unit: '',
  city: '',
  state: '',
  zipCode: ''
};

const formatLocationCardAddress = (fields, fallback = '') => {
  if (!fields) return fallback;
  const parts = [];
  if (fields.venueName?.trim()) parts.push(fields.venueName.trim());
  const street = fields.streetAddress?.trim();
  const unit = fields.unit?.trim();
  if (street && unit) parts.push(`${street}, ${unit}`);
  else if (street) parts.push(street);
  else if (unit) parts.push(unit);
  if (fields.city?.trim()) parts.push(fields.city.trim());
  if (fields.state?.trim() && fields.zipCode?.trim()) {
    parts.push(`${fields.state.trim()} ${fields.zipCode.trim()}`);
  }
  return parts.join(', ') || fallback;
};

const getGoogleVenueName = (data, suggestion) => {
  const candidates = [
    suggestion?.structured_formatting?.main_text,
    data?.name,
  ].filter(Boolean);

  return candidates.find((candidate) => !isAddressLikeGoogleName(candidate, data?.formatted_address)) || '';
};

/**
 * LocationSelector - Two-mode location picker
 * Mode 1: Search verified venues only
 * Mode 2: Full AddressHandler for Google/manual entry
 * 
 * Used by both EventPostingWizard and EditEvent for consistent UX
 * 
 * @param {Object} value - Current location data
 * @param {string} value.address - Address string
 * @param {Object} value.coordinates - { lat, lng }
 * @param {Object} value.venue - Venue object if selected
 * @param {Object} value.addressFields - Parsed address fields
 * @param {Function} onChange - Callback when location changes
 * @param {Object} userLocation - User's current location for nearby search
 * @param {Array} mapMarkers - Optional markers to show on map (for EditEvent)
 * @param {boolean} showMap - Whether to show map in AddressHandler
 * @param {boolean} recommendCheckbox - Show recommend checkbox (for EditEvent)
 */
const LocationSelector = forwardRef(({ 
  value = {},
  onChange,
  userLocation,
  mapMarkers,
  showMap = true,
  recommendCheckbox = false
}, ref) => {
  // 3-step flow: 'venues' -> 'google' -> 'map'
  // 'address' = edit selected Google address (fields only)
  // 'busking' = drop/move a pin (map only)
  const [searchMode, setSearchMode] = useState('venues'); // 'venues' | 'google' | 'map' | 'address' | 'busking'
  const locationSnapshotRef = useRef(null);
  const [venueSearch, setVenueSearch] = useState('');
  const [venueResults, setVenueResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimerRef = useRef(null);
  
  // Google search state (for step 2)
  const [googleSearch, setGoogleSearch] = useState('');
  const [googleResults, setGoogleResults] = useState([]);
  const [isGoogleSearching, setIsGoogleSearching] = useState(false);
  const googleSearchTimerRef = useRef(null);
  
  // Map state (for step 3)
  const mapRef = useRef(null);
  const [mapReady, setMapReady] = useState(false);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);
  
  // Address editing state (for step 3)
  const [addressFields, setAddressFields] = useState({
    venueName: '',
    streetAddress: '',
    unit: '',
    city: '',
    state: '',
    zipCode: ''
  });
  
  // Validation errors for address fields
  const [addressErrors, setAddressErrors] = useState({
    venueName: '',
    streetAddress: '',
    unit: '',
    city: '',
    state: '',
    zipCode: ''
  });

  // Expose methods for parent components (EditEvent compatibility)
  useImperativeHandle(ref, () => ({
    getState: () => {
      // Format the address from addressFields if available to ensure proper format
      const formattedAddress = value.addressFields 
        ? formatLocationCardAddress(value.addressFields) 
        : (value.address || '');
      const derivedAddressFields = value.addressFields || (
        !value.venue && formattedAddress ? parseAddressString(formattedAddress) : null
      );
      return {
        venue: value.venue || null,
        location: value.coordinates ? {
          type: "Point",
          coordinates: [value.coordinates.lng, value.coordinates.lat]
        } : null,
        address: formattedAddress,
        addressFields: derivedAddressFields
      };
    },
    getVenueId: () => {
      // Return venue ID if a verified venue is selected
      if (value.venue?._id) {
        return value.venue._id;
      }
      return null;
    },
    setState: (data) => {
      if (data.venue) {
        // If venue is set, use it
        onChange({
          address: data.address || data.venue?.address || '',
          coordinates: data.location?.coordinates ? {
            lat: data.location.coordinates[1],
            lng: data.location.coordinates[0]
          } : null,
          venue: data.venue,
          addressFields: data.addressFields || null
        });
      } else if (data.address || data.location) {
        // Prefilled locations should show the selected card; active edits use map mode.
        setSearchMode(data.showSelectedCard ? 'venues' : 'map');
        onChange({
          address: typeof data.address === 'object' ? data.address : data.address || '',
          coordinates: data.location?.coordinates ? {
            lat: data.location.coordinates[1],
            lng: data.location.coordinates[0]
          } : null,
          venue: null,
          addressFields: data.addressFields || (typeof data.address === 'object' ? data.address : null)
        });
      }
    },
    setErrors: () => {
      // No-op - errors are shown inline in the map step
    },
    recommend: () => {
      // No-op - recommend checkbox not used in new flow
    },
    // Validate current address and return result
    validate: () => {
      // If venue is selected, it's always valid
      if (value.venue) {
        return { isValid: true, errors: {} };
      }
      // Validate from value.addressFields
      if (value.addressFields) {
        return validateAddressComponents(value.addressFields);
      }
      return { isValid: false, errors: { city: 'city required', state: 'state required', zipCode: 'zip code required' } };
    },
    // Get validation state without triggering touch
    getValidationState: () => {
      if (value.venue) {
        return { isValid: true, errors: {} };
      }
      if (value.addressFields) {
        return validateAddressComponents(value.addressFields);
      }
      return { isValid: false, errors: { city: 'city required', state: 'state required', zipCode: 'zip code required' } };
    }
  }), [value, onChange]);

  // Search verified venues via API
  const searchVenues = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setVenueResults([]);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axiosInstance.get(`/venues/search?q=${encodeURIComponent(query)}`);
      setVenueResults(response.data.venues || []);
    } catch (error) {
      console.error('Error searching venues:', error);
      setVenueResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  const handleVenueSearchChange = useCallback((e) => {
    const query = e.target.value;
    setVenueSearch(query);
    
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      searchVenues(query);
    }, 400);
  }, [searchVenues]);

  const handleClearVenueSearch = useCallback(() => {
    clearTimeout(searchTimerRef.current);
    setVenueSearch('');
    setVenueResults([]);
    setIsSearching(false);
  }, []);

  // Select a verified venue
  const handleVenueSelect = useCallback((venue) => {
    onChange({
      address: venue.address || '',
      coordinates: venue.location?.coordinates ? {
        lat: venue.location.coordinates[1],
        lng: venue.location.coordinates[0]
      } : null,
      venue: venue,
      addressFields: null,
      source: null
    });
    setVenueSearch('');
    setVenueResults([]);
  }, [onChange]);

  // Clear selection
  const handleClearSelection = useCallback(() => {
    onChange({
      address: '',
      coordinates: null,
      venue: null,
      addressFields: null,
      source: null
    });
    setSearchMode('venues');
    setGoogleSearch('');
    setGoogleResults([]);
    setAddressFields({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
    setAddressErrors({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
  }, [onChange]);

  // Google Places search (step 2)
  const searchGoogle = useCallback(async (query) => {
    if (!query || query.trim().length < 2) {
      setGoogleResults([]);
      return;
    }
    
    setIsGoogleSearching(true);
    try {
      let url = `${backendUrl}/influencer/geolocation/autocomplete?${new URLSearchParams({ input: query })}`;
      if (userLocation) {
        url += `&latitude=${userLocation.lat}&longitude=${userLocation.lng}&radius=50000`;
      }
      const response = await axiosInstance.get(url);
      // Filter out verified venues (those with _id) - only show Google results
      const googleOnly = (response.data || []).filter(s => !s._id);
      setGoogleResults(googleOnly);
    } catch (error) {
      console.error('Error searching Google:', error);
      setGoogleResults([]);
    } finally {
      setIsGoogleSearching(false);
    }
  }, [userLocation]);

  // Debounced Google search
  const handleGoogleSearchChange = useCallback((e) => {
    const query = e.target.value;
    setGoogleSearch(query);
    
    clearTimeout(googleSearchTimerRef.current);
    googleSearchTimerRef.current = setTimeout(() => {
      searchGoogle(query);
    }, 500);
  }, [searchGoogle]);

  const handleClearGoogleSearch = useCallback(() => {
    clearTimeout(googleSearchTimerRef.current);
    setGoogleSearch('');
    setGoogleResults([]);
    setIsGoogleSearching(false);
  }, []);

  const handleFindOnGoogle = useCallback(() => {
    const query = venueSearch.trim();
    setSearchMode('google');
    if (query) {
      setGoogleSearch(query);
      searchGoogle(query);
    }
  }, [searchGoogle, venueSearch]);

  // Select a Google result
  const handleGoogleSelect = useCallback(async (suggestion) => {
    if (!suggestion) return;
    
    setIsGoogleSearching(true);
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/influencer/geolocation/place?${new URLSearchParams({
          place_id: suggestion.place_id,
        })}`
      );
      
      if (!data) return;
      
      const googleAddress = parseGoogleAddress(data.address_components);
      const venueName = getGoogleVenueName(data, suggestion);
      const newAddressFields = {
        ...googleAddress,
        unit: googleAddress.unit || '',
        venueName
      };
      setAddressFields(newAddressFields);
      
      onChange({
        address: data.formatted_address,
        coordinates: {
          lat: data.geometry.location.lat,
          lng: data.geometry.location.lng
        },
        venue: null,
        addressFields: newAddressFields,
        source: null
      });
      
      setGoogleSearch('');
      setGoogleResults([]);
      // Go back to venues mode to show the selected card
      setSearchMode('venues');
    } catch (error) {
      console.error('Error selecting Google place:', error);
    } finally {
      setIsGoogleSearching(false);
    }
  }, [onChange]);

  // Map click handler (step 3)
  const handleMapClick = useCallback(async (latlng) => {
    // Immediately set location so marker appears
    onChange({
      address: value.address || '',
      coordinates: { lat: latlng.lat, lng: latlng.lng },
      venue: null,
      addressFields: value.addressFields || addressFields,
      source: 'pin'
    });
    
    // Reverse geocode to get address
    setIsReverseGeocoding(true);
    try {
      const { data } = await axiosInstance.get(
        `${backendUrl}/influencer/geolocation/reverse?${new URLSearchParams({
          latitude: latlng.lat,
          longitude: latlng.lng,
        })}`
      );
      
      if (data?.address_components) {
        const googleAddress = parseGoogleAddress(data.address_components);
        const newAddressFields = {
          venueName: '',
          unit: '',
          ...googleAddress
        };
        
        setAddressFields(newAddressFields);
        
        // Validate and show any errors
        const { errors } = validateAddressComponents(newAddressFields);
        setAddressErrors(errors);
        
        onChange({
          address: data.formatted_address,
          coordinates: { lat: latlng.lat, lng: latlng.lng },
          venue: null,
          addressFields: newAddressFields,
          source: 'pin'
        });
      }
    } catch (error) {
      console.error('Reverse geocode error:', error);
    } finally {
      setIsReverseGeocoding(false);
    }
  }, [onChange, value.address, value.addressFields, addressFields]);

  // Handle address field changes (step 3) - validate immediately
  const handleAddressFieldChange = useCallback((field, newValue) => {
    const newFields = { ...addressFields, [field]: newValue };
    setAddressFields(newFields);
    
    // Validate immediately and update errors
    const { errors } = validateAddressComponents(newFields);
    setAddressErrors(errors);
    
    onChange({
      address: formatLocationCardAddress(newFields),
      coordinates: value.coordinates,
      venue: null,
      addressFields: newFields,
      source: value.source || null
    });
  }, [addressFields, onChange, value.coordinates]);

  // Pan map when coordinates change (with safety check)
  useEffect(() => {
    if (mapReady && mapRef.current && value.coordinates) {
      try {
        mapRef.current.panTo({
          lat: value.coordinates.lat,
          lng: value.coordinates.lng
        });
      } catch (e) {
        // Map not ready yet, ignore
      }
    }
  }, [value.coordinates, mapReady]);

  // When a venue is selected (from map marker popup), switch back to venues mode
  useEffect(() => {
    if (value.venue && (searchMode === 'map' || searchMode === 'busking')) {
      setSearchMode('venues');
    }
  }, [value.venue, searchMode]);

  const captureLocationSnapshot = useCallback(() => {
    locationSnapshotRef.current = {
      address: value.address,
      coordinates: value.coordinates ? { ...value.coordinates } : null,
      venue: value.venue,
      addressFields: value.addressFields ? { ...value.addressFields } : null,
      source: value.source || null
    };
  }, [value.address, value.coordinates, value.venue, value.addressFields, value.source]);

  const restoreLocationSnapshot = useCallback(() => {
    const snap = locationSnapshotRef.current;
    if (snap) {
      onChange({
        address: snap.address || '',
        coordinates: snap.coordinates,
        venue: snap.venue,
        addressFields: snap.addressFields,
        source: snap.source || null
      });
      const fields = snap.addressFields || EMPTY_ADDRESS_FIELDS;
      setAddressFields(fields);
      setAddressErrors(snap.addressFields ? validateAddressComponents(fields).errors : {
        venueName: '',
        streetAddress: '',
        unit: '',
        city: '',
        state: '',
        zipCode: ''
      });
    }
    setSearchMode('venues');
  }, [onChange]);

  const hydrateAddressFields = useCallback(() => {
    let fields = { ...EMPTY_ADDRESS_FIELDS };

    if (value.addressFields) {
      fields = {
        venueName: value.addressFields.venueName || '',
        streetAddress: value.addressFields.streetAddress || '',
        unit: value.addressFields.unit || '',
        city: value.addressFields.city || '',
        state: value.addressFields.state || '',
        zipCode: value.addressFields.zipCode || ''
      };
    } else if (value.address) {
      fields = parseAddressString(value.address);
      onChange({
        address: value.address,
        coordinates: value.coordinates,
        venue: null,
        addressFields: fields,
        source: value.source || null
      });
    }

    setAddressFields(fields);
    setAddressErrors(validateAddressComponents(fields).errors);
  }, [value.addressFields, value.address, value.coordinates, onChange]);

  // Enter address-edit mode (fields only)
  const handleEditLocation = useCallback(() => {
    captureLocationSnapshot();
    hydrateAddressFields();
    setSearchMode('address');
  }, [captureLocationSnapshot, hydrateAddressFields]);

  const handleBuskingLocation = useCallback(() => {
    captureLocationSnapshot();
    hydrateAddressFields();
    setSearchMode('busking');
  }, [captureLocationSnapshot, hydrateAddressFields]);

  // Only show selected state when we have actual coordinates (a complete selection)
  // But not if we're in google mode or map mode
  // For non-venue selections, show card if we have coordinates and some address info
  // (validation happens on form submit, not here)
  const hasValidSelection = useMemo(() => {
    if (!value.coordinates) return false;
    if (searchMode === 'google' || searchMode === 'map' || searchMode === 'address' || searchMode === 'busking') return false;
    
    // Venues are always valid
    if (value.venue) return true;
    
    // For custom locations, show the card if we have addressFields or address string
    // Don't require full validation here - just need some address info to display
    if (value.addressFields) {
      // Has at least some address data (city or street or venue name)
      return !!(value.addressFields.city || value.addressFields.streetAddress || value.addressFields.venueName);
    }
    
    // If no addressFields but has address string, consider it valid for display
    return !!value.address;
  }, [value.coordinates, value.venue, value.addressFields, value.address, searchMode]);
  
  if (hasValidSelection) {
    return (
      <div className="location-selector">
        <div className="ui-label mb-kit-8">location</div>
        <div className="location-selector__selected">
          <Card inverted className="location-selector__selected-card">
            <div className="location-selector__selected-info">
              {/* For venues: show venue name as title, address below in lighter color */}
              {/* For custom locations: show address as primary (no "custom location" title) */}
              {value.source === 'pin' && !value.venue ? (
                <>
                  <div className="location-selector__selected-name">
                    custom pin drop, closest to:
                  </div>
                  <div className="location-selector__selected-pin-address">
                    {value.addressFields
                      ? formatLocationCardAddress(value.addressFields, value.address)
                      : value.address}
                  </div>
                </>
              ) : (value.venue?.name || value.addressFields?.venueName) ? (
                <>
                  <div className="location-selector__selected-name">
                    {value.venue?.name || value.addressFields?.venueName}
                  </div>
                  <div className="location-selector__selected-address">
                    {value.addressFields 
                      ? formatLocationCardAddress(value.addressFields, value.address) 
                      : value.address}
                  </div>
                </>
              ) : (
                <div className="location-selector__selected-name">
                  {value.addressFields 
                    ? formatLocationCardAddress(value.addressFields, value.address) 
                    : value.address}
                </div>
              )}
            </div>
            <div className="location-selector__actions location-selector__actions--with-badge">
              {value.venue ? (
                <div className="location-selector__verified-badge">
                  <img
                    src={Logo}
                    alt="verified"
                    className="location-selector__verified-logo"
                  />
                  verified
                </div>
              ) : (
                <div className="location-selector__google-badge">
                  <FcGoogle size={14} />
                  <span>google maps</span>
                </div>
              )}
              <IconButton variant="ghost" label="clear location" icon={<IoClose />} onClick={handleClearSelection} />
            </div>
          </Card>
          {!value.venue && (
            <div className="location-selector__selected-actions">
              <KitButton variant="tertiary" size="sm" onClick={handleEditLocation}>
                add suite, floor or edit
              </KitButton>
              <KitButton variant="tertiary" size="sm" onClick={handleBuskingLocation}>
                busking? drop a pin here
              </KitButton>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="location-selector">
      <div className="ui-label mb-kit-8">location</div>
      {searchMode === 'venues' ? (
        <>
          {/* Step 1: Venue Search Mode */}
          <div className="location-selector__venue-search">
            <Input
              placeHolder="search venue, business or address"
              value={venueSearch}
              onChange={handleVenueSearchChange}
              InlineComp={() =>
                venueSearch ? (
                  <IconButton variant="ghost" label="clear search" icon={<IoClose />} onClick={handleClearVenueSearch} />
                ) : null
              }
            />

            {!(venueSearch.length >= 2 && !isSearching) && (
            <div className="flex justify-center">
              <KitButton variant="tertiary" size="sm" onClick={handleBuskingLocation}>
                no physical address / busking
              </KitButton>
            </div>
            )}

            {venueSearch.trim().length > 0 && (
            <div className="location-selector__venue-intro">
              <div className="location-selector__venue-hint">
                select from our verified locations (if listed):
              </div>
            </div>
            )}
            
            {/* Search Results */}
            {(venueResults.length > 0 || isSearching) && (
              <div className={`location-selector__venue-results${venueResults.length >= 2 && !isSearching ? ' location-selector__venue-results--multi' : ''}`}>
                {isSearching ? (
                  <div className="location-selector__loading">searching venues...</div>
                ) : (
                  venueResults.map((venue) => (
                    <Card
                      key={venue._id}
                      interactive
                      className="location-selector__venue-card"
                      onClick={() => handleVenueSelect(venue)}
                    >
                      <div className="location-selector__venue-badge">
                        <img
                          src={Logo}
                          alt="verified"
                          className="location-selector__venue-badge-logo"
                        />
                        verified
                      </div>
                      <div className="location-selector__venue-name">{venue.name}</div>
                      <div className="location-selector__venue-address">{venue.address}</div>
                      {venue.neighborhood && (
                        <div className="location-selector__venue-neighborhood">{venue.neighborhood}</div>
                      )}
                    </Card>
                  ))
                )}
              </div>
            )}
            
            {/* No results message */}
            {venueSearch.length >= 2 && !isSearching && venueResults.length === 0 && (
              <div className="location-selector__no-results">
                no verified venues found for "{venueSearch}"
              </div>
            )}
          </div>
          
          {/* Toggle to Google search (step 2) */}
          {venueSearch.length >= 2 && !isSearching && (
          <div className="location-selector__toggle-section location-selector__toggle-section--inline">
            <div className="location-selector__fallback-prompt">
              don't see it?
            </div>
            <div className="location-selector__maps-stack">
              <KitButton variant="secondary" size="sm" icon={<FcGoogle />} onClick={handleFindOnGoogle}>
                find it on google maps
              </KitButton>
              <KitButton variant="tertiary" size="sm" onClick={handleBuskingLocation}>
                no physical address / busking
              </KitButton>
            </div>
          </div>
          )}
        </>
      ) : searchMode === 'google' ? (
        <>
          {/* Step 2: Google Search Mode */}
          <div className="location-selector__google-search">
            <div className="location-selector__step-back">
              <IconButton variant="ghost" label="back" icon={<IoChevronBack />} onClick={() => setSearchMode('venues')} />
            </div>
            
            <Input
              placeHolder="search for a location..."
              value={googleSearch}
              onChange={handleGoogleSearchChange}
              InlineComp={() =>
                googleSearch ? (
                  <IconButton variant="ghost" label="clear search" icon={<IoClose />} onClick={handleClearGoogleSearch} />
                ) : null
              }
            />
            
            {/* Google Results */}
            {(googleResults.length > 0 || isGoogleSearching) && (
              <div className="location-selector__google-results">
                {isGoogleSearching ? (
                  <div className="location-selector__loading">
                    <Loader />
                  </div>
                ) : (
                  googleResults.map((suggestion, i) => (
                    <Card
                      key={i}
                      interactive
                      className="location-selector__google-card"
                      onClick={() => handleGoogleSelect(suggestion)}
                    >
                      <div className="location-selector__google-result-badge">
                        <FcGoogle size={14} />
                        maps
                      </div>
                      <div className="location-selector__google-name">
                        {suggestion.structured_formatting?.main_text || suggestion.description}
                      </div>
                      {suggestion.structured_formatting?.secondary_text && (
                        <div className="location-selector__google-address">
                          {suggestion.structured_formatting.secondary_text}
                        </div>
                      )}
                    </Card>
                  ))
                )}
              </div>
            )}
            
            {/* No results */}
            {googleSearch.length >= 2 && !isGoogleSearching && googleResults.length === 0 && (
              <div className="location-selector__no-results">
                no results found for "{googleSearch}"
              </div>
            )}
          </div>
          
          {/* Toggle to map mode (step 3) */}
          <div className="location-selector__toggle-section">
            <div className="location-selector__fallback-prompt">
              still can't find? busking?
            </div>
            <KitButton variant="tertiary" size="sm" onClick={() => setSearchMode('map')}>
              drop a pin on the map
            </KitButton>
          </div>
        </>
      ) : searchMode === 'map' ? (
        <>
          {/* Step 3: Map Pin Drop Mode */}
          <div className="location-selector__map-mode">
            <div className="location-selector__step-back">
              <IconButton variant="ghost" label="back" icon={<IoChevronBack />} onClick={() => setSearchMode('google')} />
              <div className="ui-label">drop a pin</div>
            </div>
            
            <div className="text-kit-sm opacity-70 mb-kit-12">
              tap the map to place your location pin
            </div>
            
            {/* Map */}
            <div className="location-selector__map-container">
              <DashboardMap
                className="w-full h-full"
                Ref={mapRef}
                userLocation={userLocation}
                style={{ borderRadius: 'var(--inyc-input-border-radius)' }}
                onMapReady={() => setMapReady(true)}
                onMapClick={handleMapClick}
                markers={[
                  value.coordinates ? {
                    zIndexOffset: 1000,
                    icon: eventMarker,
                    lat: value.coordinates.lat,
                    lng: value.coordinates.lng,
                    props: {
                      Popup: (props) => (
                        <Popup {...props} className="bg-[--inyc-primary]">
                          <div className="bg-[--inyc-primary] p-kit-8 min-w-[12rem] rounded-kit-xs">
                            <div className="text-kit-xs mb-kit-8 text-[--inyc-primary-text-color]">
                              {formatAddressComponents(addressFields) || 'pin dropped'}
                            </div>
                            <MarkerPopupButton
                              Icon={MdClear}
                              text="clear"
                              iconsize={13}
                              onClick={(e) => {
                                e.stopPropagation();
                                onChange({
                                  address: '',
                                  coordinates: null,
                                  venue: null,
                                  addressFields: null,
                                  source: null
                                });
                                setAddressFields({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
                                setAddressErrors({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
                                props.onClose?.();
                              }}
                            />
                          </div>
                        </Popup>
                      )
                    }
                  } : null,
                  ...(mapMarkers || [])
                ].filter(Boolean)}
              />
              {isReverseGeocoding && (
                <div className="location-selector__map-loading">
                  <Loader />
                </div>
              )}
            </div>
            
            {/* Address editing (shown after pin drop) */}
            {value.coordinates && (
              <div className="location-selector__address-edit">
                <div className="location-selector__address-title">
                  <span>address details</span>
                </div>
                
                <p className="location-selector__address-note">
                  address is approximate — please verify and adjust if needed
                </p>
                
                <div className="location-selector__address-fields">
                  <Input
                    placeHolder="venue / business name (optional)"
                    value={addressFields.venueName}
                    onChange={(e) => handleAddressFieldChange('venueName', e.target.value)}
                    error={addressErrors.venueName}
                  />
                  <Input
                    placeHolder="street address (optional)"
                    value={addressFields.streetAddress}
                    onChange={(e) => handleAddressFieldChange('streetAddress', e.target.value)}
                    error={addressErrors.streetAddress}
                  />
                  <Input
                    placeHolder="apt / floor / suite / room (optional)"
                    value={addressFields.unit}
                    onChange={(e) => handleAddressFieldChange('unit', e.target.value)}
                    error={addressErrors.unit}
                  />
                  <div className="location-selector__address-row">
                    <Input
                      placeHolder="city"
                      value={addressFields.city}
                      onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                      error={addressErrors.city}
                    />
                    <Input
                      placeHolder="state"
                      value={addressFields.state}
                      onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                      error={addressErrors.state}
                    />
                  </div>
                  <Input
                    placeHolder="zip code"
                    value={addressFields.zipCode}
                    onChange={(e) => handleAddressFieldChange('zipCode', e.target.value)}
                    error={addressErrors.zipCode}
                  />
                </div>
              </div>
            )}
          </div>
          
          {/* Done and Cancel buttons */}
          <div className="location-selector__map-actions">
            <KitButton variant="secondary" onClick={() => {
                // Clear map state and go back
                onChange({
                  address: '',
                  coordinates: null,
                  venue: null,
                  addressFields: null,
                  source: null
                });
                setAddressFields({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
                setAddressErrors({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
                setSearchMode('venues');
              }}>
              cancel
            </KitButton>
            <KitButton variant="primary" disabled={!value.coordinates || !validateAddressComponents(addressFields).isValid}
              onClick={() => {
                // Validate and go back to show selected card
                const { isValid } = validateAddressComponents(addressFields);
                if (value.coordinates && isValid) {
                  setSearchMode('venues');
                }
              }}>
              done
            </KitButton>
          </div>
        </>
      ) : searchMode === 'address' ? (
        <>
          <div className="location-selector__address-edit">
            <div className="location-selector__address-title">
              <FiEdit2 size={14} />
              <span>address details</span>
            </div>
            
            <p className="location-selector__address-note">
              address is approximate — please verify and adjust if needed
            </p>
            
            <div className="location-selector__address-fields">
              <Input
                placeHolder="venue / business name (optional)"
                value={addressFields.venueName}
                onChange={(e) => handleAddressFieldChange('venueName', e.target.value)}
                error={addressErrors.venueName}
              />
              <Input
                placeHolder="street address (optional)"
                value={addressFields.streetAddress}
                onChange={(e) => handleAddressFieldChange('streetAddress', e.target.value)}
                error={addressErrors.streetAddress}
              />
              <Input
                placeHolder="apt / floor / suite / room (optional)"
                value={addressFields.unit}
                onChange={(e) => handleAddressFieldChange('unit', e.target.value)}
                error={addressErrors.unit}
              />
              <div className="location-selector__address-row">
                <Input
                  placeHolder="city"
                  value={addressFields.city}
                  onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                  error={addressErrors.city}
                />
                <Input
                  placeHolder="state"
                  value={addressFields.state}
                  onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                  error={addressErrors.state}
                />
              </div>
              <Input
                placeHolder="zip code"
                value={addressFields.zipCode}
                onChange={(e) => handleAddressFieldChange('zipCode', e.target.value)}
                error={addressErrors.zipCode}
              />
            </div>
          </div>
          
          <div className="location-selector__map-actions">
            <KitButton variant="secondary" onClick={restoreLocationSnapshot}>
              cancel
            </KitButton>
            <KitButton
              variant="primary"
              disabled={!value.coordinates || !validateAddressComponents(addressFields).isValid}
              onClick={() => {
                const { isValid } = validateAddressComponents(addressFields);
                if (value.coordinates && isValid) {
                  onChange({
                    address: formatLocationCardAddress(addressFields),
                    coordinates: value.coordinates,
                    venue: null,
                    addressFields: addressFields,
                    source: value.source || null
                  });
                  setSearchMode('venues');
                }
              }}
            >
              done
            </KitButton>
          </div>
        </>
      ) : searchMode === 'busking' ? (
        <>
          <div className="location-selector__map-mode">
            <div className="text-kit-sm opacity-70 mb-kit-12">
              tap the map to place your location pin
            </div>
            
            <div className="location-selector__map-container">
              <DashboardMap
                className="w-full h-full"
                Ref={mapRef}
                userLocation={userLocation}
                style={{ borderRadius: 'var(--inyc-input-border-radius)' }}
                onMapReady={() => setMapReady(true)}
                onMapClick={handleMapClick}
                markers={[
                  value.coordinates ? {
                    zIndexOffset: 1000,
                    icon: eventMarker,
                    lat: value.coordinates.lat,
                    lng: value.coordinates.lng,
                    props: {
                      Popup: (props) => (
                        <Popup {...props} className="bg-[--inyc-primary]">
                          <div className="bg-[--inyc-primary] p-kit-8 min-w-[12rem] rounded-kit-xs">
                            <div className="text-kit-xs mb-kit-8 text-[--inyc-primary-text-color]">
                              {formatAddressComponents(addressFields) || 'pin dropped'}
                            </div>
                            <MarkerPopupButton
                              Icon={MdClear}
                              text="clear"
                              iconsize={13}
                              onClick={(e) => {
                                e.stopPropagation();
                                onChange({
                                  address: '',
                                  coordinates: null,
                                  venue: null,
                                  addressFields: null,
                                  source: null
                                });
                                setAddressFields({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
                                setAddressErrors({ venueName: '', streetAddress: '', unit: '', city: '', state: '', zipCode: '' });
                                props.onClose?.();
                              }}
                            />
                          </div>
                        </Popup>
                      )
                    }
                  } : null,
                  ...(mapMarkers || [])
                ].filter(Boolean)}
              />
              {isReverseGeocoding && (
                <div className="location-selector__map-loading">
                  <Loader />
                </div>
              )}
            </div>
          </div>
          
          <div className="location-selector__map-actions">
            <KitButton variant="secondary" onClick={restoreLocationSnapshot}>
              cancel
            </KitButton>
            <KitButton variant="primary" disabled={!value.coordinates}
              onClick={() => {
                if (value.coordinates) {
                  setSearchMode('venues');
                }
              }}>
              done
            </KitButton>
          </div>
        </>
      ) : null}
    </div>
  );
});

export default LocationSelector;

